import { DevTool } from "@hookform/devtools";
import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Box, Button, Divider, Stack } from "@mui/material";
import { useFormPersistence } from "hooks/useFormPersistence";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSteps } from "react-step-builder";
import { useMembersStore } from "../stores";
import { academicStatusSchema } from "../validation";
import { AcademicStatusSection, JobStatusSection } from "./sections";

export function AcademicStatusForm({ isEditMode }) {
  const memberData = useMembersStore(state => state.memberData);
  const setMemberData = useMembersStore(state => state.setMemberData);
  const { t } = useTranslation();
  const { next, prev } = useSteps();
  const { saveFormData, loadFormData } = useFormPersistence();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    control
  } = useForm({
    defaultValues: {
      education_and_skills: memberData.education_and_skills
        ? memberData.education_and_skills
        : [
            {
              academic_status: "",
              professional_skill: ""
            }
          ],
      job_status: memberData.job_status ? memberData.job_status : "",
      custom_job_status: memberData.custom_job_status
        ? memberData.custom_job_status
        : "",
      profession: memberData.profession ? memberData.profession : "",
      work_place: memberData.work_place ? memberData.work_place : "",
      monthly_income: memberData.monthly_income ? memberData.monthly_income : ""
    },
    resolver: yupResolver(academicStatusSchema)
  });

  // Load saved form data when component mounts
  useEffect(() => {
    const savedData = loadFormData();
    if (savedData && savedData.academicStatus) {
      reset(savedData.academicStatus);
    }
  }, [loadFormData, reset]);

  // Save form data whenever it changes
  useEffect(() => {
    const subscription = watch(data => {
      saveFormData({
        academicStatus: data
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, saveFormData]);

  const onSubmit = data => {
    setMemberData(data);
    next();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DevTool control={control} />
      <AcademicStatusSection
        register={register}
        errors={errors}
        control={control}
      />
      <Divider sx={{ my: 3 }} />
      <JobStatusSection register={register} errors={errors} watch={watch} />
      {isEditMode ? (
        <Box sx={{ mt: 6 }} />
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 8 }}
        >
          <Button
            type="button"
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={prev}
          >
            {t("form.common.back")}
          </Button>
          <Button type="submit" variant="contained" endIcon={<ArrowForward />}>
            {t("form.common.next")}
          </Button>
        </Stack>
      )}
    </form>
  );
}
